<template>
  <sg-popper ref="popperRef">
    <sg-tooltip-trigger
      :disabled="disabled"
      :trigger="trigger"
      :virtual-ref="virtualRef"
      :virtual-triggering="virtualTriggering"
    >
      <slot v-if="$slots.default" />
    </sg-tooltip-trigger>
    <sg-tooltip-content
      ref="contentRef"
      :aria-label="ariaLabel"
      :boundaries-padding="boundariesPadding"
      :content="content"
      :disabled="disabled"
      :effect="theme"
      :enterable="enterable"
      :fallback-placements="fallbackPlacements"
      :hide-after="hideAfter"
      :gpu-acceleration="gpuAcceleration"
      :offset="offset"
      :verticalOffset="verticalOffset"
      :persistent="persistent"
      :popper-class="dropClassName"
      :popper-style="popperStyle"
      :placement="placement"
      :popper-options="popperOptions"
      :pure="pure"
      :raw-content="rawContent"
      :reference-sg="referenceEl"
      :show-after="compatShowAfter"
      :strategy="strategy"
      :transfer="transfer"
      :transition="transition"
      :max-width="maxWidth"
      :z-index="zIndex"
      :render-content-type="renderContentType"
    >
      <slot name="content">
        <span v-if="rawContent" v-html="content"></span>
        <span v-else>{{ limitContent }}</span>
      </slot>
      <sg-popper-arrow v-if="compatShowArrow" :arrow-offset="arrowOffset" />
    </sg-tooltip-content>
  </sg-popper>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  provide,
  toRef,
  readonly,
  unref,
} from 'vue'
import {
  SgPopper,
  SgPopperArrow,
} from '@sgui-plus/components/popper'

import { isBool } from '@sgui-plus/utils/util'
import {
  usePopperContainer,
  useId,
  useDelayedToggle,
} from '@sgui-plus/hooks'
import SgTooltipContent from './content.vue'
import SgTooltipTrigger from './trigger.vue'
import {
  useTooltipProps,
  useTooltipEmits,
  useTooltipModelToggle,
} from './tooltip'
import { TOOLTIP_INJECTION_KEY } from './constants'

export default defineComponent({
  name: 'SgTooltip',
  components: {
    SgPopper,
    SgPopperArrow,
    SgTooltipContent,
    SgTooltipTrigger,
  },
  props: useTooltipProps,
  emits: useTooltipEmits,
  setup(props, { emit }) {
    usePopperContainer()

    const compatShowAfter = computed(() => {
      return props.openDelay || (props.showAfter as number)
    })
    const compatShowArrow = computed(() => {
      return isBool(props.visibleArrow) ? props.visibleArrow : props.showArrow
    })

    const id = useId()
    const popperRef = ref<InstanceType<typeof SgPopper> | null>(null)
    const contentRef = ref<any>()

    const updatePopper = () => {
      const popperComponent = unref(popperRef)
      if (popperComponent) {
        popperComponent.popperInstanceRef?.update()
      }
    }
    const open = ref(false)

    const { show, hide } = useTooltipModelToggle({
      indicator: open,
    })

    const limitContent = computed(() => {
      const isOver = props.content.length > props.maxLength
      return isOver ? props.content.substring(0, props.maxLength) + '...' : props.content
    })

    const { onOpen, onClose } = useDelayedToggle({
      showAfter: compatShowAfter,
      hideAfter: toRef(props, 'hideAfter'),
      autoClose: props.autoHide ? toRef(props, 'hideDelay') : ref(0),
      open: show,
      close: hide,
    })

    const isFocusInsideContent = () => {
      const popperContent: HTMLElement | undefined =
        contentRef.value?.contentRef?.popperContentRef
      return popperContent && popperContent.contains(document.activeElement)
    }

    const controlled = computed(() => isBool(props.visible))

    provide(TOOLTIP_INJECTION_KEY, {
      controlled,
      id,
      open: readonly(open),
      trigger: toRef(props, 'trigger'),
      onOpen,
      onClose,
      onToggle: () => {
        if (unref(open)) {
          onClose()
        } else {
          onOpen()
        }
      },
      onShow: () => {
        emit('show')
      },
      onHide: () => {
        emit('hide')
      },
      onBeforeShow: () => {
        emit('before-show')
      },
      onBeforeHide: () => {
        emit('before-hide')
      },
      updatePopper,
    })

    return {
      limitContent,
      compatShowAfter,
      compatShowArrow,
      popperRef,
      contentRef,
      isFocusInsideContent,
      open,
      hide,
      updatePopper,
      onOpen,
      onClose,
    }
  },
})
</script>
